import { parseNumericAmount, resolveProductId } from "./couponMath";

/**
 * Same multi-product shape as checkout_api FormData product_id[] / product_price[].
 * One entry per selected product (repeated by quantity). Single product stays a scalar.
 */
export const buildStripeOrderProductFields = (
  products = [],
  { freeOrder = false, combinedOriginalPrice } = {}
) => {
  const productIds = [];
  const productPrices = [];
  const lineOriginals = [];

  (Array.isArray(products) ? products : []).forEach((product) => {
    const rawId = resolveProductId(product);
    if (!rawId) return;
    const qty = Math.max(1, parseInt(product.quantity, 10) || 1);
    const unit = parseNumericAmount(
      product?.product_price ?? product?.price ?? product?.m_price ?? 0
    );
    const unitPrice = Number.isFinite(unit) && unit > 0 ? unit.toFixed(2) : "0.00";
    for (let i = 0; i < qty; i += 1) {
      productIds.push(rawId);
      productPrices.push(freeOrder ? "0.00" : unitPrice);
      lineOriginals.push(unitPrice);
    }
  });

  const combined =
    combinedOriginalPrice != null && combinedOriginalPrice !== ""
      ? String(combinedOriginalPrice)
      : lineOriginals
          .reduce((sum, price) => sum + (parseNumericAmount(price) || 0), 0)
          .toFixed(2);

  if (productIds.length === 0) {
    return {
      productId: "",
      productPrice: freeOrder ? "0.00" : null,
      originalPrice: combined,
      billingModelId: "3",
    };
  }

  if (productIds.length === 1) {
    return {
      productId: productIds[0],
      productPrice: productPrices[0],
      originalPrice: combined,
      billingModelId: "3",
    };
  }

  return {
    productId: productIds,
    productPrice: productPrices,
    originalPrice: combined,
    billingModelId: productIds.map(() => "3"),
  };
};

/**
 * Build JSON body for POST /idunrx/api/createOrder_stripe
 */
export const buildCreateOrderStripeBody = ({
  email,
  firstName,
  lastName,
  phoneNumber,
  address,
  aptSuite,
  city,
  stateCode,
  postalCode,
  sameAsShipping,
  address2,
  city2,
  billingStateCode,
  postalCode2,
  appliedDiscount,
  productId,
  paymentToken,
  uniqueId,
  contactDetailsId,
  campaignId,
  productPrice,
  originalPrice,
  billingModelId,
  qty,
  smsNonMarketingConsent,
  smsMarketingConsent,
  checkoutLinkId,
}) => {
  const body = {
    email,
    first_name: firstName,
    last_name: lastName,
    phone: phoneNumber,
    address,
    city_name: city,
    state_name: stateCode,
    zip_code: postalCode,
    start_url: window.location.href,
    payment_token: paymentToken,
    product_id: productId,
    address2: aptSuite || "",
    billingSameAsShipping: sameAsShipping ? "YES" : "NO",
    promo_codes: appliedDiscount || null,
  };

  if (uniqueId) body.unique_id = uniqueId;
  if (contactDetailsId) body.contact_details_id = contactDetailsId;
  if (campaignId) body.campaign_id = campaignId;
  if (productPrice != null) body.product_price = productPrice;
  if (originalPrice != null) body.original_price = originalPrice;
  if (billingModelId) body.billing_model_id = billingModelId;
  if (qty) body.qty = qty;
  if (smsNonMarketingConsent != null) {
    body.sms_non_marketing_consent = smsNonMarketingConsent ? "YES" : "NO";
  }
  if (smsMarketingConsent != null) {
    body.sms_marketing_consent = smsMarketingConsent ? "YES" : "NO";
  }
  if (checkoutLinkId) body.checkout_link_id = checkoutLinkId;

  if (sameAsShipping) {
    body.billing_address = address;
    body.billing_address2 = aptSuite || "";
    body.billing_city_name = city;
    body.billing_state_name = stateCode;
    body.billing_zip_code = postalCode;
  } else {
    body.billing_address = address2 || "";
    body.billing_address2 = "";
    body.billing_city_name = city2 || "";
    body.billing_state_name = billingStateCode || "";
    body.billing_zip_code = postalCode2 || "";
  }

  return body;
};
